import type { GeographicScope } from "./trip-intent";

export type TripComplexityInput = {
  durationHours?: number;
  geographicScope?: GeographicScope;
  namedRoute?: string;
  requiredPlaces?: string[];
  mode?: string;
  notes?: string;
};

export type TripComplexity = {
  level: "standard" | "advanced";
  score: number;
  reasons: string[];
};

const normalize = (value: string) => value
  .toLowerCase()
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "");

export function classifyTripComplexity(input: TripComplexityInput): TripComplexity {
  let score = 0;
  const reasons: string[] = [];
  const notes = normalize(input.notes ?? "");
  const duration = input.durationHours ?? 0;

  if (input.geographicScope === "multi_city") {
    score += 3;
    reasons.push("multi_city");
  } else if (input.geographicScope === "regional") {
    score += 2;
    reasons.push("regional");
  } else if (input.geographicScope === "named_route") {
    score += 1;
    reasons.push("named_route");
  }

  if (duration >= 72) {
    score += 3;
    reasons.push("three_or_more_days");
  } else if (duration >= 48) {
    score += 2;
    reasons.push("two_or_more_days");
  } else if (duration >= 24) {
    score += 1;
    reasons.push("multi_day");
  }

  if ((input.requiredPlaces?.length ?? 0) >= 8) {
    score += 2;
    reasons.push("many_mandatory_places");
  }

  if (/hotel|hoteis|alojamento|dormir|pernoita|overnight|accommodation/.test(notes)) {
    score += 2;
    reasons.push("accommodation_planning");
  }
  if (/etapas?|stage|stages|dividir por dias|dia a dia/.test(notes)) {
    score += 2;
    reasons.push("daily_stages");
  }
  if (/combustivel|gasolina|diesel|fuel|portagens|tolls|carregamento|charging/.test(notes)) {
    score += 1;
    reasons.push("road_logistics");
  }
  if (/inicio diferente|fim diferente|comeca em .* termina em|start in .* end in/.test(notes)) {
    score += 1;
    reasons.push("different_start_end");
  }

  if (input.namedRoute && duration < 24 && input.geographicScope === "named_route") {
    score = Math.max(0, score - 1);
    reasons.push("short_named_route");
  }

  return {
    level: score >= 4 ? "advanced" : "standard",
    score,
    reasons,
  };
}
